import { useForm } from 'react-hook-form';

import { Button } from 'components/Button/Button';
import { ExternalLink } from 'components/ExternalLink/ExternalLink';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { Select } from 'components/Select/Select';

import { SourceConfig } from './types';

type SourceCredentialsFormProps = {
  config: SourceConfig;
  onSubmit: (credentials: Record<string, string>) => void;
  isLoading: boolean;
  error?: string | null;
};

export function SourceCredentialsForm({
  config,
  onSubmit,
  isLoading,
  error,
}: SourceCredentialsFormProps) {
  const defaultValues: Record<string, string> = {};
  for (const field of config.credentialFields) {
    if (field.defaultValue) {
      defaultValues[field.name] = field.defaultValue;
    }
  }

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ defaultValues });

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col"
    >
      {config.credentialFields.map((field) => {
        if (field.type === 'select' && field.options) {
          const value = watch(field.name);
          return (
            <Field key={field.name} label={field.label} id={field.name} className="!my-1.5">
              <Select
                id={field.name}
                options={field.options}
                value={field.options.find((o) => o.value === value) ?? null}
                onChange={(option) => {
                  if (option) {
                    setValue(field.name, option.value);
                  }
                }}
              />
              {field.helpText && (
                <p className="text-xs text-[var(--ui-fg-muted)] mt-1">
                  {field.helpText}
                </p>
              )}
            </Field>
          );
        }

        return (
          <Field key={field.name} label={field.label} id={field.name} className="!my-1.5">
            <Input
              id={field.name}
              type={field.type === 'password' ? 'password' : 'text'}
              placeholder={field.placeholder}
              error={Boolean(errors[field.name])}
              {...register(field.name, {
                required: field.required ? `${field.label} is required` : false,
              })}
            />
            {errors[field.name]?.message && (
              <p className="form-error theme danger">
                {errors[field.name]?.message as string}
              </p>
            )}
            {field.helpText && (
              <p className="text-xs text-[var(--ui-fg-muted)] mt-1">
                {field.helpText}
              </p>
            )}
            {field.helpUrl && (
              <ExternalLink href={field.helpUrl} className="text-xs mt-1">
                Get API key
              </ExternalLink>
            )}
          </Field>
        );
      })}

      {error && <p className="form-error theme danger">{error}</p>}

      <Button type="submit" variant="primary" loading={isLoading}>
        Fetch cameras
      </Button>
    </form>
  );
}
